// Cost telemetry — tracks per-turn spend and rolls up daily totals.
//
// Hooks the agent loop's `usage` LoopEvent into a Zustand store. Two
// values matter to the user:
//   - todayTotalUsd: rolling sum for the current YYYY-MM-DD (resets at
//     midnight local time).
//   - lastTurn: most recent generation's spend, for the toast.
//
// Persistence: only the dailyHistory array goes to IDB so it survives
// reload + tab close. sessionTotalUsd is runtime only — every fresh
// page load starts at $0 for the session.

import { create } from 'zustand';

import { idbGet, idbSet } from '../lib/idb';
import { computeCostUsd, formatUsd } from '../core/cost/pricing';
import type { UsageReport } from '../core/agent/types';

const DAILY_HISTORY_KEY = 'cost-daily-history';
const HISTORY_RETENTION_DAYS = 60;

export interface DailyTotal {
  /** ISO date in user's local TZ ('YYYY-MM-DD'). */
  date: string;
  totalUsd: number;
  /** Total tokens consumed that day (input + output). */
  totalTokens: number;
  /** How many generations that day. */
  generations: number;
}

export interface LastTurnRecord {
  model: string;
  usd: number;
  tokens: number;
  inputTokens: number;
  outputTokens: number;
  at: string;
}

interface CostStore {
  hydrating: boolean;
  todayTotalUsd: number;
  todayTokens: number;
  todayGenerations: number;
  sessionTotalUsd: number;
  dailyHistory: DailyTotal[];
  lastTurn: LastTurnRecord | null;

  hydrate: () => Promise<void>;
  /** Called from the agent loop when a 'usage' LoopEvent arrives. */
  recordUsage: (model: string, usage: UsageReport) => Promise<void>;
  /** Acknowledge / dismiss the last-turn record so the toast unmounts. */
  clearLastTurn: () => void;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export const useCost = create<CostStore>((set, get) => ({
  hydrating: true,
  todayTotalUsd: 0,
  todayTokens: 0,
  todayGenerations: 0,
  sessionTotalUsd: 0,
  dailyHistory: [],
  lastTurn: null,

  hydrate: async () => {
    set({ hydrating: true });
    const stored = (await idbGet<DailyTotal[]>(DAILY_HISTORY_KEY)) ?? [];
    // Trim very old entries so the store doesn't bloat indefinitely.
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - HISTORY_RETENTION_DAYS);
    const cutoffIso = cutoff.toISOString().slice(0, 10);
    const trimmed = stored.filter((d) => d.date >= cutoffIso);

    const today = todayIso();
    const todayRecord = trimmed.find((d) => d.date === today);
    set({
      hydrating: false,
      dailyHistory: trimmed,
      todayTotalUsd: todayRecord?.totalUsd ?? 0,
      todayTokens: todayRecord?.totalTokens ?? 0,
      todayGenerations: todayRecord?.generations ?? 0,
    });
  },

  recordUsage: async (model, usage) => {
    const usd = computeCostUsd(model, usage);
    const tokens = usage.inputTokens + usage.outputTokens;
    const today = todayIso();
    const state = get();
    const history = [...state.dailyHistory];
    let todayRec = history.find((d) => d.date === today);
    if (!todayRec) {
      todayRec = { date: today, totalUsd: 0, totalTokens: 0, generations: 0 };
      history.push(todayRec);
    }
    todayRec.totalUsd += usd;
    todayRec.totalTokens += tokens;
    todayRec.generations += 1;
    history.sort((a, b) => a.date.localeCompare(b.date));

    const lastTurn: LastTurnRecord = {
      model,
      usd,
      tokens,
      inputTokens: usage.inputTokens,
      outputTokens: usage.outputTokens,
      at: new Date().toISOString(),
    };

    set({
      dailyHistory: history,
      todayTotalUsd: todayRec.totalUsd,
      todayTokens: todayRec.totalTokens,
      todayGenerations: todayRec.generations,
      sessionTotalUsd: state.sessionTotalUsd + usd,
      lastTurn,
    });
    // Persist daily history to IDB. Don't await on the hot path inside
    // the loop's onEvent flow — the recordUsage caller has its own
    // promise to await on.
    await idbSet(DAILY_HISTORY_KEY, history);
  },

  clearLastTurn: () => set({ lastTurn: null }),
}));

export { formatUsd };
